/**
 * @description 工具函数
 */
import { Navigator } from '@hb/h5-navigator';
import { env, Native } from '@hb/NBridge/lib/h5';
// eslint-disable-next-line import/no-extraneous-dependencies
import { jumpWx } from '@/pages/mall/common/utils/jumpUrl';
import configEnv from '@/const/config';

/**
 * @description 序列化对象
 * @function safeJsonStringify
 * @param {*} obj 对象
 * @returns
 */
export function safeJsonStringify(obj) {
  try {
    return JSON.stringify(obj);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`safeJsonStringify ${obj} => error`, error);
    return '';
  }
}

/**
 *
 * @param {*} url 跳转目标页面路由
 * @param {string} [entryPage='index'] 入口页面名称，如 'pr_index_bounty_detail_default'，默认 'index'
 * @returns  完整路由
 */
export function getCurEnvFullUrl(url, entryPage = 'index') {
  // 跳转本地h5时，使用本地h5地址
  if (configEnv.env === 'dev') {
    return `${window.location.origin}/#/${url}`;
  }

  const getEnvMark = () => {
    if (configEnv.env === 'pro') return '';
    if (configEnv.env === 'dev') return 'fat/';
    return `${configEnv.env}/`;
  };
  const envMark = getEnvMark();
  return `https://m.hellobike.com/AppPlatformH5/${envMark}latest/${entryPage}.html#/${url}`;
}

/**
 * @description 地址跳转
 * @function jumpUrl
 * @param {*} url 地址
 * @returns
 */
export function jumpUrl(url = '') {
  if (!url) return;

  try {
    if (env.isWechatMini) {
      jumpWx(url);
    } else if (env.isAliMP || env.isAliMPMonitor) {
      // 支付宝端跳转
      Native.callNative({
        method: 'goOperationUrlV2',
        params: url,
      });
    } else if (env.isEasyBikeApp) {
      // 哈啰App端跳转
      Navigator.go(url);
    } else {
      window.location.href = url;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`jumpUrl ${url} => error`, safeJsonStringify(error));
  }
}

/**
 * @description 判断是否是今天
 * @function timestampIsToday
 * @param {*} timestamp 时间戳
 * @returns
 */
export function timestampIsToday(timestamp) {
  if (!timestamp) return false;
  return new Date(timestamp).toDateString() === new Date().toDateString();
}

/**
 * @description 版本比较
 * @function compareVersions
 * @param {*} v1 当前版本
 * @param {*} v2 比较版本
 * @returns 0:版本一致、-1:版本较小、1:版本较大
 */
export function compareVersions(v1, v2) {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);

  const maxLength = Math.max(parts1.length, parts2.length);

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < maxLength; i++) {
    const num1 = parts1[i] || 0;
    const num2 = parts2[i] || 0;

    if (num1 > num2) return 1;
    if (num1 < num2) return -1;
  }

  return 0;
}

/**
 * 检查微信是否安装
 */
export const checkWXInstallation = async () => {
  try {
    const checkRes = await Native.callNative({
      classMap: 'business/ebike',
      method: 'hlr_isAppInstalled',
      params: { appName: 'wechat' },
    });
    return !!checkRes.isInstalled;
  } catch (error) {
    return false;
  }
};
/**
 * 检查小红书是否安装
 */
export const checkXHSInstallation = async () => {
  try {
    const checkRes = await Native.callNative({
      classMap: 'business/hitch',
      method: 'checkThirdAppInstall',
      params: { schemeUrl: 'xhsdiscover://home' },
      timeout: false,
    });
    return checkRes && checkRes.install;
  } catch (error) {
    return false;
  }
};

/**
 * 生成随机uuid
 * @returns {string} uuid
 */
export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    if (c === 'x') return r.toString(16);
    // y 位：值在 8~b（即 8~11），不使用位运算
    return ((r % 4) + 8).toString(16);
  });
}

/**
 * 轮循方法
 * 入参
 * @typedef {Object} Option
 * @property {Function} requestFn 请求方法，注意，需要用一个函数包裹，例如：() => request.get(url)
 * @property {Number} interval 轮循间隔时间，毫秒
 * @property {Number} count 轮循次数, 如果与timeout同时设置，则以先满足的为准
 * @property { Number } timeout 最大轮循时长, 如果与count同时设置，则以先满足的为准, 秒（整数）
 * @property {Function} callback 回调方法 如果该方法返回true，则停止轮循
 * @param { Option } options
 * 返回值
 * @typedef { Object } StartPollingInterface
 * @property { Function } stop 停止轮循方法
 */
export const startPolling = (options) => {
  const {
    requestFn, interval = 2000, count = 10, timeout = Infinity, callback,
  } = options;
  let startCount = 0;
  const startTime = Date.now();
  const intervalId = setInterval(async () => {
    const timeLong = Math.floor((Date.now() - startTime) / 1000);
    if (startCount >= count || timeLong > Math.ceil(timeout)) {
      callback({
        end: true,
        code: 0,
        data: {},
        msg: '轮循结束',
      });
      clearInterval(intervalId);
      return;
    }
    startCount += 1;
    let stopTip = false; // 是否停止轮循，如果callback返回true，则停止轮循
    try {
      const res = await requestFn();
      stopTip = callback(res);
    } catch (error) {
      stopTip = callback({
        code: 101,
        data: {},
        msg: '网络请求失败',
      });
    }
    if (stopTip) {
      clearInterval(intervalId);
    }
  }, interval);

  return {
    stop() {
      clearInterval(intervalId);
    },
  };
};

/**
 * @description 获取rem和px单位转化比例
 * @function getUnit
 * @returns
 */
export const getUnit = () => {
  const STANDARD_WIDTH = 750; // 标准宽度
  // 临时适配折叠屏
  const computerMediaWidth = window.innerWidth > 700 ? 750 : 375;
  return (
    (100 * (STANDARD_WIDTH ? Math.min(750, window.innerWidth) : window.innerWidth))
    / computerMediaWidth
  );
};

/**
 * @description 预加载图片
 * @function preloadImage
 * @param {string} url 图片url
 * @returns {Promise<string>} 返回promise对象
 */
export function preloadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    let timerId = null;

    const cleanup = () => {
      img.onload = null;
      img.onerror = null;
      if (timerId) {
        clearTimeout(timerId);
        timerId = null;
      }
    };

    img.onload = () => {
      cleanup();
      // eslint-disable-next-line no-console
      console.info('preloadImage => success', url);
      resolve(url);
    };

    img.onerror = () => {
      cleanup();
      // eslint-disable-next-line no-console
      console.warn('preloadImage => error', url);
      reject(new Error(`Failed to preload image: ${url}`));
    };

    // 设置10秒超时
    timerId = setTimeout(() => {
      cleanup();
      // eslint-disable-next-line no-console
      console.warn('preloadImage => timeout', url);
      reject(new Error(`Preload timeout: ${url}`));
    }, 10000);

    img.src = url;
  });
}

/**
 * @description 批量预加载图片
 * @function preloadImages
 * @param {string[]} urls 图片url列表
 * @returns {Promise<string[]>} 返回promise对象
 */
export function preloadImages(urls) {
  if (!urls || urls.length === 0) return Promise.resolve();
  return Promise.all(urls.map((url) => preloadImage(url).catch(() => {})));
}

/**
 * @description 判断时间戳是否在指定天数内(疲劳度控制)
 * @function isWithinDays
 * @param {number|string} timestamp 时间戳
 * @param {number} days 天数
 * @returns {boolean} 是否在指定天数内
 */
export function isWithinDays(timestamp, days) {
  if (!timestamp) return false;
  const targetTime = new Date(timestamp).getTime();
  if (Number.isNaN(targetTime)) return false;

  const now = Date.now();
  const diff = now - targetTime;
  const limit = days * 24 * 60 * 60 * 1000;

  return diff < limit;
}

/**
 * @description 锚点跳转
 * @function anchorToEl
 * @param {string} elementId 元素id
 * @param {number} offset 偏移量
 * @returns {void}
 */
export function anchorToEl(elementId, offset = 0) {
  try {
    const element = document.getElementById(elementId);
    if (!element) return;
    const { offsetTop } = element;
    window.scrollTo({
      top: offsetTop - offset,
      behavior: 'smooth',
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`scrollTo ${elementId} => error`, safeJsonStringify(error));
  }
}

// 获取当前环境端口
export const getPort = () => {
  if (env.isWechatMini) {
    return '2';
  }
  if (env.isAliMP || env.isAliMPMonitor) {
    return '1';
  }
  if (env.isEasyBikeApp) {
    return '0';
  }
  return '';
};

export default {};
